import {
  AccountItems,
  AccountTransfer,
  AccountSettings,
  AppState,
  BankAccount,
  BudgetItem,
  ExpenseRule,
  IncomeRule,
  InterestBand,
  InterestSettings,
  LOW_BALANCE_DAYS,
  LowBalanceAlert,
  PLAN_SCHEMA_VERSION,
  Plan,
  PlanSettings,
  RagThresholds,
  ShopMonth,
  Transfer,
} from '../types'
import type { AuthUser } from './api'
import { toISODate } from './dateRules'
import { makeId } from './id'

/**
 * State shaping and the last traces of the browser-only era.
 *
 * Saved state now lives on the server (see `useSyncedDocument`). What stays
 * here is the shape of a brand-new plan, the normalisation every inbound
 * document goes through, and readers for the two localStorage keys the app
 * used before it had accounts — kept so that anyone who has been using it can
 * carry their plan across instead of retyping it.
 */

// These still carry the app's old name, and have to. They are the keys the
// plan was written under; renaming them wouldn't rename what's in anyone's
// browser, it would just make it unreachable.
const LEGACY_STORAGE_KEY = 'cashflow-calendar:state:v1'
const LEGACY_SANDBOX_KEY = 'cashflow-calendar:sandbox:v1'

/**
 * The id given to the one account a version-1 document becomes. Fixed rather
 * than random so that the main plan and the sandbox — migrated separately, and
 * possibly on different days — still agree on which account is which. The
 * server migration uses the same value.
 */
export const MIGRATED_ACCOUNT_ID = 'primary'
const MIGRATED_ACCOUNT_NAME = 'Current account'

const DEFAULT_RAG: RagThresholds = { bad: 0, medium: 300 }

function emptyItems(): AccountItems {
  return { incomes: [], expenses: [], budgets: [], oneOffs: [], adjustments: [], actuals: [], targets: [] }
}

/** A blank account, for "Add account". */
export function newBankAccount(name: string, settings?: Partial<AccountSettings>): BankAccount {
  return {
    id: makeId(),
    name,
    includeInTotal: true,
    settings: {
      startingBalance: 0,
      startingBalanceDate: toISODate(new Date()),
      ragThresholds: { ...DEFAULT_RAG },
      ...settings,
    },
    ...emptyItems(),
  }
}

const EXAMPLE_INCOME: Omit<IncomeRule, 'id'> = {
  kind: 'income',
  name: 'Salary',
  amount: 2800,
  frequency: 'monthly',
  dateRule: { type: 'working_day_before', dayOfMonth: 25 },
  active: true,
}
const EXAMPLE_EXPENSE: Omit<ExpenseRule, 'id'> = {
  kind: 'expense',
  name: 'Rent',
  amount: 1200,
  frequency: 'monthly',
  dateRule: { type: 'exact', dayOfMonth: 1 },
  active: true,
  category: 'Housing',
}
const EXAMPLE_BUDGET: Omit<BudgetItem, 'id'> = {
  kind: 'budget',
  name: 'Groceries',
  amount: 90,
  frequency: 'weekly',
  dayOfWeek: 5,
  category: 'Food',
  active: true,
}

/** The plan a brand-new sign-up opens on: one account with enough in it to show what the app does. */
export function defaultPlan(): Plan {
  const account = newBankAccount(MIGRATED_ACCOUNT_NAME, { startingBalance: 2000 })
  account.incomes = [{ id: makeId(), ...EXAMPLE_INCOME }]
  account.expenses = [{ id: makeId(), ...EXAMPLE_EXPENSE }]
  account.budgets = [{ id: makeId(), ...EXAMPLE_BUDGET }]
  return {
    schemaVersion: PLAN_SCHEMA_VERSION,
    settings: { currencySymbol: '£', projectHistory: false, combinedRagThresholds: { ...DEFAULT_RAG } },
    accounts: [account],
    transfers: [],
  }
}

/**
 * One of the starter plan's examples, as it was put there: a statement import
 * replaces these rather than adding a second salary beside them (SM-31). Any
 * change to one makes it the person's own.
 */
export function isExampleItem(item: IncomeRule | ExpenseRule | BudgetItem): boolean {
  const example = item.kind === 'income' ? EXAMPLE_INCOME : item.kind === 'expense' ? EXAMPLE_EXPENSE : EXAMPLE_BUDGET
  const { id: _id, ...rest } = item
  return JSON.stringify(sortKeys(rest)) === JSON.stringify(sortKeys(example))
}

function sortKeys(value: object): object {
  return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
}

/** Nothing in this account yet but the examples, or nothing at all: the moment to offer starting from a statement. */
export function onlyExamples(state: AccountItems): boolean {
  return (
    [...state.incomes, ...state.expenses, ...state.budgets].every(isExampleItem) &&
    state.oneOffs.length === 0 &&
    state.actuals.length === 0
  )
}

type Loose = Record<string, unknown>

function isObject(value: unknown): value is Loose {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function listOr<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function ragOr(value: unknown, fallback: RagThresholds): RagThresholds {
  if (!isObject(value)) return { ...fallback }
  return { bad: numberOr(value.bad, fallback.bad), medium: numberOr(value.medium, fallback.medium) }
}

function itemsFrom(raw: Loose): AccountItems {
  const shopSpending = listOr<ShopMonth>(raw.shopSpending)
  return {
    incomes: listOr(raw.incomes),
    expenses: listOr(raw.expenses),
    budgets: listOr(raw.budgets),
    oneOffs: listOr(raw.oneOffs),
    adjustments: listOr(raw.adjustments),
    actuals: listOr(raw.actuals),
    targets: listOr(raw.targets),
    // Left off until there is some, so plans that never import stay as they were.
    ...(shopSpending.length > 0 ? { shopSpending } : {}),
  }
}

function accountSettingsFrom(raw: unknown): AccountSettings {
  const s = isObject(raw) ? raw : {}
  return {
    startingBalance: numberOr(s.startingBalance, 0),
    startingBalanceDate:
      typeof s.startingBalanceDate === 'string' && s.startingBalanceDate
        ? s.startingBalanceDate
        : toISODate(new Date()),
    ragThresholds: ragOr(s.ragThresholds, DEFAULT_RAG),
  }
}

/**
 * Keeps interest settings the engine can trust: rates with a date and at
 * least one band, bands in order with only the last one open-ended. Dropped
 * altogether when there's nothing usable, which the engine reads as "no interest".
 */
function interestFrom(raw: unknown): InterestSettings | undefined {
  if (!isObject(raw)) return undefined
  const day = raw.paymentDay
  const paymentDay =
    day === 'last' ? 'last' : typeof day === 'number' && day >= 1 && day <= 31 ? Math.floor(day) : 1
  let undated = false
  const rates = listOr<unknown>(raw.rates)
    .filter(isObject)
    .map((r) => ({
      id: typeof r.id === 'string' && r.id ? r.id : makeId(),
      from: typeof r.from === 'string' && r.from !== '' ? r.from : undefined,
      bands: normalizeBands(listOr<unknown>(r.bands)),
    }))
    // Only one rate can apply "from the start"; a second undated one would
    // have nothing to say when it takes over.
    .filter((r) => {
      if (r.from) return true
      if (undated) return false
      undated = true
      return true
    })
    .filter((r) => r.bands.length > 0)
    .map((r) => (r.from ? r : { id: r.id, bands: r.bands }))
  return { enabled: raw.enabled === true, paymentDay, rates }
}

/** Sorted, bounded bands with the open-ended one last. Exported for the form, which saves through it too. */
export function normalizeBands(raw: unknown[]): InterestBand[] {
  const bands = raw
    .filter(isObject)
    .map((b) => ({ upTo: typeof b.upTo === 'number' && b.upTo > 0 ? b.upTo : undefined, rate: numberOr(b.rate, 0) }))
  const bounded = bands
    .filter((b): b is { upTo: number; rate: number } => b.upTo !== undefined)
    .sort((a, b) => a.upTo - b.upTo)
    // Two bands with the same ceiling would leave the second one empty.
    .filter((b, i, all) => i === 0 || b.upTo > all[i - 1].upTo)
  const rest = bands.find((b) => b.upTo === undefined) ?? { rate: 0 }
  if (bounded.length === 0 && bands.length === 0) return []
  return [...bounded, { rate: rest.rate }]
}

const FREQUENCIES: ReadonlySet<unknown> = new Set(['once', 'monthly', 'weekly', 'four_weekly', 'yearly'])

/**
 * Transfers the engine can act on: between two different accounts the plan
 * still has, with a schedule it understands. Anything else is dropped rather
 * than kept half-working — a transfer to an account that's gone would take
 * money out of one side and put it nowhere.
 */
function transfersFrom(raw: unknown, accountIds: ReadonlySet<string>): Transfer[] {
  const seen = new Set<string>()
  const out: Transfer[] = []
  for (const t of listOr<unknown>(raw)) {
    if (!isObject(t)) continue
    const from = t.fromAccountId
    const to = t.toAccountId
    if (typeof from !== 'string' || typeof to !== 'string' || from === to) continue
    if (!accountIds.has(from) || !accountIds.has(to)) continue
    if (!FREQUENCIES.has(t.frequency)) continue
    if (t.frequency === 'once' && (typeof t.date !== 'string' || !t.date)) continue
    let id = typeof t.id === 'string' && t.id ? t.id : makeId()
    if (seen.has(id)) id = makeId()
    seen.add(id)
    out.push({
      ...(t as unknown as Transfer),
      id,
      kind: 'transfer',
      name: typeof t.name === 'string' && t.name.trim() ? t.name : 'Transfer',
      amount: Math.abs(numberOr(t.amount, 0)),
      active: t.active !== false,
    })
  }
  return out
}

/**
 * The low-balance warning, kept only when it's usable: a whole number of days
 * in range, a threshold that's a number or absent. Anything else is left out,
 * which reads as the default.
 */
function alertFrom(raw: unknown): { lowBalanceAlert?: LowBalanceAlert } {
  if (!isObject(raw)) return {}
  const days = typeof raw.days === 'number' && Number.isFinite(raw.days) ? Math.round(raw.days) : NaN
  if (!(days >= LOW_BALANCE_DAYS.min && days <= LOW_BALANCE_DAYS.max)) return {}
  const threshold = typeof raw.threshold === 'number' && Number.isFinite(raw.threshold) ? raw.threshold : undefined
  return { lowBalanceAlert: { enabled: raw.enabled !== false, days, ...(threshold !== undefined ? { threshold } : {}) } }
}

/**
 * Version 1 → 2: the single-account document becomes the plan's one account.
 *
 * Kept in step with the server migration (backend/migrations/
 * Version20260927120000.php), which upgrades everything already stored. This
 * copy is for documents that never went through the server — an export file
 * from before the upgrade, a pre-accounts localStorage plan, or an offline
 * cache written by the previous build.
 */
function migrateV1(raw: Loose): Plan {
  const settings = isObject(raw.settings) ? raw.settings : {}
  return {
    schemaVersion: PLAN_SCHEMA_VERSION,
    settings: {
      currencySymbol: typeof settings.currencySymbol === 'string' && settings.currencySymbol ? settings.currencySymbol : '£',
      projectHistory: settings.projectHistory === true,
      // With one account the combined balance *is* that account's, so its
      // thresholds carry over unchanged.
      combinedRagThresholds: ragOr(settings.ragThresholds, DEFAULT_RAG),
    },
    accounts: [
      {
        id: MIGRATED_ACCOUNT_ID,
        name: MIGRATED_ACCOUNT_NAME,
        includeInTotal: true,
        settings: accountSettingsFrom(settings),
        ...itemsFrom(raw),
      },
    ],
    transfers: [],
  }
}

/**
 * Fills in whatever a stored or imported plan is missing so that data written
 * by an older version of the app — or hand-edited JSON — can't reach the engine
 * half-shaped. Every path that takes a plan from outside the running app (the
 * server, the local cache, Import data) must go through here: a missing list
 * is not a recoverable error further down, it's a crash.
 *
 * Anything without an `accounts` list is taken to be a version-1 document and
 * upgraded.
 */
export function normalizePlan(parsed: unknown): Plan {
  if (!isObject(parsed)) return defaultPlan()
  if (!Array.isArray(parsed.accounts)) return migrateV1(parsed)

  const settings = isObject(parsed.settings) ? parsed.settings : {}
  const seen = new Set<string>()
  const accounts: BankAccount[] = []
  for (const raw of parsed.accounts) {
    if (!isObject(raw)) continue
    // A duplicated id would make every per-account edit land on both copies.
    let id = typeof raw.id === 'string' && raw.id ? raw.id : makeId()
    if (seen.has(id)) id = makeId()
    seen.add(id)
    const interest = interestFrom(raw.interest)
    accounts.push({
      id,
      name: typeof raw.name === 'string' && raw.name.trim() ? raw.name : 'Untitled account',
      includeInTotal: raw.includeInTotal !== false,
      settings: accountSettingsFrom(raw.settings),
      ...itemsFrom(raw),
      ...(interest ? { interest } : {}),
      ...(typeof raw.statementFingerprint === 'string' && raw.statementFingerprint
        ? { statementFingerprint: raw.statementFingerprint }
        : {}),
    })
  }
  if (accounts.length === 0) accounts.push(newBankAccount(MIGRATED_ACCOUNT_NAME))

  // Version 2 had no transfers; reading it as an empty list is the whole upgrade.
  const transfers = transfersFrom(parsed.transfers, new Set(accounts.map((a) => a.id)))

  return {
    schemaVersion: PLAN_SCHEMA_VERSION,
    settings: {
      currencySymbol: typeof settings.currencySymbol === 'string' && settings.currencySymbol ? settings.currencySymbol : '£',
      projectHistory: settings.projectHistory === true,
      combinedRagThresholds: ragOr(settings.combinedRagThresholds, DEFAULT_RAG),
      ...alertFrom(settings.lowBalanceAlert),
    },
    accounts,
    transfers,
  }
}

/**
 * One account as the projection engine sees it: its own lists and settings,
 * with the plan-wide ones folded in, and its side of every transfer that
 * touches it. Everything that works on "a plan" — the engine, the sidebar, the
 * forms — works on this, and never needs to know other accounts exist.
 */
export function accountState(plan: Plan | PlanSettings, account: BankAccount): AppState {
  const settings: PlanSettings = 'accounts' in plan ? plan.settings : plan
  const transfers = 'accounts' in plan ? transfersFor(plan, account.id) : []
  return {
    ...account,
    settings: {
      ...account.settings,
      currencySymbol: settings.currencySymbol,
      projectHistory: settings.projectHistory,
    },
    transfers,
  }
}

/** One account's side of each transfer in or out of it. */
export function transfersFor(plan: Plan, accountId: string): AccountTransfer[] {
  const nameOf = (id: string) => plan.accounts.find((a) => a.id === id)?.name ?? 'another account'
  const out: AccountTransfer[] = []
  for (const t of plan.transfers ?? []) {
    if (t.fromAccountId === accountId) out.push({ ...t, direction: 'out', counterpartyName: nameOf(t.toAccountId) })
    else if (t.toAccountId === accountId) out.push({ ...t, direction: 'in', counterpartyName: nameOf(t.fromAccountId) })
  }
  return out
}

/**
 * What Export data writes: everything the server holds for the account, so the
 * same file answers a data-access request and moves a plan to another account.
 * The password hash is left out — it's of no use to anyone outside the server.
 */
export interface AccountExport {
  export: typeof EXPORT_MARKER
  exportedAt: string
  account: Pick<AuthUser, 'email' | 'verified' | 'createdAt'>
  plan: Plan
  /** Null when there's no sandbox, or it couldn't be fetched (offline). */
  sandbox: Plan | null
}

const EXPORT_MARKER = 'snapshot-money'

export function buildExport(user: AuthUser, plan: Plan, sandbox: Plan | null, now: Date = new Date()): AccountExport {
  return {
    export: EXPORT_MARKER,
    exportedAt: now.toISOString(),
    account: { email: user.email, verified: user.verified, createdAt: user.createdAt },
    plan,
    sandbox,
  }
}

export function exportAsJson(data: AccountExport): string {
  return JSON.stringify(data, null, 2)
}

/**
 * The plan inside a file chosen for Import data: a full export, or a bare plan
 * from before exports carried the account too (and from before multiple
 * accounts, which normalising upgrades).
 */
export function planFromImport(parsed: unknown): Plan {
  if (isObject(parsed) && parsed.export === EXPORT_MARKER) return normalizePlan(parsed.plan)
  return normalizePlan(parsed)
}

export function clonePlan(plan: Plan): Plan {
  return JSON.parse(JSON.stringify(plan))
}

// ---- Migration from the pre-accounts version ----

export interface LegacyLocalData {
  main: Plan | null
  sandbox: Plan | null
}

/**
 * Reads the plan this browser had before accounts existed. Returns nulls when
 * there is nothing to carry across, which is the normal case for a new person
 * on a new device.
 */
export function readLegacyLocalData(): LegacyLocalData {
  return {
    main: readLegacyKey(LEGACY_STORAGE_KEY),
    sandbox: readLegacyKey(LEGACY_SANDBOX_KEY),
  }
}

function readLegacyKey(key: string): Plan | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return normalizePlan(JSON.parse(raw))
  } catch (e) {
    console.error(`Couldn't read the old saved data at ${key}.`, e)
    return null
  }
}

/**
 * Only called once the data is safely on the server, and only when the person
 * said to import it — declining leaves it where it is, so the offer can be
 * taken up from another device later.
 */
export function clearLegacyLocalData() {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY)
    localStorage.removeItem(LEGACY_SANDBOX_KEY)
  } catch (e) {
    console.error("Couldn't clear the old saved data.", e)
  }
}
